import * as XLSX from 'xlsx';

import { QueueSheetManagementRows, ContextualNodeDetails } from '../../../interface/QueueSheetInterface';
import { getGtmStatusDisplay, getSlotPackageDisplayName } from '../../../../CreateBooking/constants/DetailedBookingConstants';

/**
 * Maps raw type-of-purchase values to their export display names.
 */
const TYPE_OF_PURCHASE_EXPORT_MAP: Record<string, string> = {
  'GP SOV': 'SOV',
  'GP Class 1': 'Impressions',
  'NGP': 'DSP',
};

const getExportTypeOfPurchase = (typeOfPurchase: string): string =>
  TYPE_OF_PURCHASE_EXPORT_MAP[typeOfPurchase] || typeOfPurchase;

export const escapeCSVValue = (value: unknown): string => {
  if (value == null) return '';
  const stringValue = String(value);
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const EXPORT_HEADERS = [
  'S. No.',
  'Brand',
  'Brand Name(Rodeo)',
  'BDM',
  'Type of booking',
  'Category',
  'Slot/Package',
  'Ad slot SOV%',
  'Purchase value',
  'Contextual node',
  'Impressions',
  'AM',
  'SF Opp. ID',
  'GTM Status',
];

export interface NameLookupMaps {
  aeNameMap?: Map<string, string>;
  amNameMap?: Map<string, string>;
}

interface ResolvedSlotNode {
  slotPackage: string;
  contextualNode: string;
}

export const resolveSlotAndNode = (
  slotOrPackageName: string,
  contextualNode: string,
): ResolvedSlotNode => {
  const isGateway = slotOrPackageName?.toLowerCase() === 'gateway';
  if (isGateway) {
    return {
      slotPackage: getSlotPackageDisplayName(contextualNode || ''),
      contextualNode: '',
    };
  }
  return {
    slotPackage: getSlotPackageDisplayName(slotOrPackageName || ''),
    contextualNode: contextualNode || '',
  };
};

export type ExportCell = string | number;

/**
 * Column indices (0-based) for numeric cells in an exported row.
 * Keep in sync with EXPORT_HEADERS above.
 */
const SOV_PERCENT_COL = 7;      // 'Ad slot SOV%'
const PURCHASE_VALUE_COL = 8;   // 'Purchase value'
const IMPRESSIONS_COL = 10;     // 'Impressions'

/** Excel number formats applied so Excel renders numeric cells correctly. */
const PERCENT_FORMAT = '0%';
const INTEGER_FORMAT = '#,##0';

const buildSegmentRow = (
  booking: QueueSheetManagementRows,
  node: ContextualNodeDetails | null,
  nameMaps?: NameLookupMaps,
): ExportCell[] => {
  const resolved = node
    ? resolveSlotAndNode(node.slotOrPackageName, node.contextualNode)
    : { slotPackage: '', contextualNode: '' };

  return [
    String(booking.entryId),
    booking.brand || '',
    booking.rodeoAdName || '',
    (nameMaps?.aeNameMap?.get(booking.aeName) ?? booking.aeName) || '',
    booking.typeOfPurchase ? getExportTypeOfPurchase(booking.typeOfPurchase) : '',
    booking.category || '',
    resolved.slotPackage,
    // Store SOV as a fraction (e.g. 0.5) so Excel's '0%' format renders it as "50%"
    // while keeping the cell a true numeric value.
    node?.percentageBooked != null ? node.percentageBooked / 100 : '',
    node?.valueBlockedAtPackageLevel != null ? Number(node.valueBlockedAtPackageLevel) : '',
    resolved.contextualNode,
    node?.impressionsBooked != null ? Number(node.impressionsBooked) : '',
    (nameMaps?.amNameMap?.get(booking.amName) ?? booking.amName) || '',
    booking.sfOpportunityId || '',
    getGtmStatusDisplay(booking.gtmStatus),
  ];
};

/**
 * Converts booking data to a 2D array (headers + rows) for Excel export.
 * Each contextual node produces one row. Bookings with no nodes get one row with empty slot fields.
 * Rows are sorted by entry ID ascending.
 */
export const convertQueueSheetToRows = (
  data: QueueSheetManagementRows[],
  nameMaps?: NameLookupMaps,
): ExportCell[][] => {
  if (!data.length) return [];

  const sortedData = [...data].sort((a, b) => a.entryId - b.entryId);

  const rows: ExportCell[][] = [EXPORT_HEADERS];
  for (const booking of sortedData) {
    const nodes = booking.contextualNodes;
    if (!nodes || nodes.length === 0) {
      rows.push(buildSegmentRow(booking, null, nameMaps));
    } else {
      for (const node of nodes) {
        rows.push(buildSegmentRow(booking, node, nameMaps));
      }
    }
  }

  return rows;
};

/**
 * Applies a number format to a worksheet cell if it holds a numeric value.
 */
const applyNumberFormat = (
  worksheet: XLSX.WorkSheet,
  rowIdx: number,
  colIdx: number,
  format: string,
): void => {
  const address = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
  const cell = worksheet[address];
  if (cell && cell.t === 'n') {
    cell.z = format;
  }
};

/**
 * Downloads a 2D array of rows as an .xlsx Excel file.
 * Handles UTF-8 natively — no BOM workaround needed.
 *
 * Numeric columns (SOV %, Purchase value, Impressions) get Excel number formats
 * so they render as true numbers in Excel rather than text.
 */
export const downloadExcel = (rows: ExportCell[][], filename: string): void => {
  try {
    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // Apply number formats to data rows (skip header row at index 0).
    for (let r = 1; r < rows.length; r++) {
      applyNumberFormat(worksheet, r, SOV_PERCENT_COL, PERCENT_FORMAT);
      applyNumberFormat(worksheet, r, PURCHASE_VALUE_COL, INTEGER_FORMAT);
      applyNumberFormat(worksheet, r, IMPRESSIONS_COL, INTEGER_FORMAT);
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

/**
 * Convenience: converts booking data and downloads as Excel in one call.
 */
export const exportBookingsToExcel = (
  data: QueueSheetManagementRows[],
  filename: string,
  nameMaps?: NameLookupMaps,
): void => {
  const rows = convertQueueSheetToRows(data, nameMaps);
  if (rows.length === 0) return;
  downloadExcel(rows, filename);
};

// ── CSV helpers (used by bulk action report) ──

export const downloadCSV = (content: string, filename: string): void => {
  try {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};
